import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useOssAuth } from "../context/OssAuthContext";
import { toast } from "sonner";
import {
  Activity,
  Droplets,
  HeartPulse,
  Loader2,
  TrendingUp,
  TrendingDown,
  Minus,
  Clock,
  PenLine,
  Heart,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CLASSIFICATION_STYLES = {
  optimal: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", badge: "bg-emerald-500" },
  normal: { bg: "bg-green-50", border: "border-green-200", text: "text-green-700", badge: "bg-green-500" },
  elevated: { bg: "bg-yellow-50", border: "border-yellow-200", text: "text-yellow-700", badge: "bg-amber-500" },
  moderate: { bg: "bg-orange-50", border: "border-orange-200", text: "text-orange-700", badge: "bg-orange-500" },
  high: { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", badge: "bg-red-600" },
  critical: { bg: "bg-red-100", border: "border-red-300", text: "text-red-800", badge: "bg-red-700" },
};

const CONFIG = {
  glicemia: {
    title: "Monitoraggio Glicemia",
    icon: Droplets,
    iconClass: "text-amber-500",
    borderClass: "border-amber-200",
    accentBtn: "border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-100",
    apiSegment: "glicemia",
    valueKey: "glicemia_mg_dl",
    chartKey: "Glicemia",
    chartColor: "#f59e0b",
    unit: "mg/dL",
    valueLabel: "Glicemia",
    parseValue: (raw) => parseFloat(String(raw).trim().replace(",", ".")),
    validateValue: (v) => !Number.isNaN(v) && v >= 20 && v <= 600,
    valueError: "Inserire un valore tra 20 e 600 mg/dL",
    inputProps: { min: 20, max: 600, step: "0.1", placeholder: "Es. 110" },
    inputLabel: "Glicemia (mg/dL)",
    inputId: "glicemia-val",
    getTrend: (list) => {
      if (!list || list.length < 2) return null;
      const diff = (list[0]?.glicemia_mg_dl ?? 0) - (list[1]?.glicemia_mg_dl ?? 0);
      if (diff > 10) return "worse";
      if (diff < -10) return "better";
      return "stable";
    },
    buildPayload: (value, extra, isoWhen) => ({
      glicemia_mg_dl: value,
      contesto: extra.contesto || "generale",
      note: extra.note?.trim() || undefined,
      data_misurazione: isoWhen,
    }),
    renderLatestValue: (m) => m.glicemia_mg_dl ?? "—",
    tableHeaders: ["Contesto", "Note"],
    tableCells: (m) => [
      m.contesto === "digiuno"
        ? "Digiuno"
        : m.contesto === "post_pasto"
          ? "Post-pasto"
          : "Generale",
      m.note || "—",
    ],
  },
  ossigenazione: {
    title: "Monitoraggio Ossigenazione",
    icon: HeartPulse,
    iconClass: "text-sky-500",
    borderClass: "border-sky-200",
    accentBtn: "border-sky-200 bg-sky-50 text-sky-900 hover:bg-sky-100",
    apiSegment: "ossigenazione",
    valueKey: "spo2_percent",
    chartKey: "SpO2",
    chartColor: "#0ea5e9",
    unit: "%",
    valueLabel: "Saturazione O₂",
    parseValue: (raw) => parseInt(String(raw).trim(), 10),
    validateValue: (v) => !Number.isNaN(v) && v >= 50 && v <= 100,
    valueError: "Inserire un valore tra 50 e 100%",
    inputProps: { min: 50, max: 100, step: "1", placeholder: "Es. 97" },
    inputLabel: "SpO₂ (%)",
    inputId: "spo2-val",
    getTrend: (list) => {
      if (!list || list.length < 2) return null;
      const diff = (list[0]?.spo2_percent ?? 0) - (list[1]?.spo2_percent ?? 0);
      if (diff < -2) return "worse";
      if (diff > 2) return "better";
      return "stable";
    },
    buildPayload: (value, extra, isoWhen) => ({
      spo2_percent: value,
      data_misurazione: isoWhen,
      ...(extra.battito !== undefined && extra.battito !== ""
        ? { battito_cardiaco: extra.battito }
        : {}),
    }),
    renderLatestValue: (m) => (
      <span>
        {m.spo2_percent ?? "—"}
        <span className="text-lg font-normal text-slate-500">%</span>
      </span>
    ),
    tableHeaders: ["Battito"],
    tableCells: (m) => [m.battito_cardiaco ?? "—"],
  },
};

function toLocalDatetimeValue(d = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

function MonitoraggioVitaleCore({ tipo, fascicoloId, token, forOss }) {
  const cfg = CONFIG[tipo];
  const Icon = cfg.icon;
  const apiBase = forOss
    ? `${API_URL}/api/oss/me/monitoraggio/${cfg.apiSegment}`
    : `${API_URL}/api/monitoraggio/${cfg.apiSegment}`;

  const [misurazioni, setMisurazioni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [manualValue, setManualValue] = useState("");
  const [manualWhen, setManualWhen] = useState(() => toLocalDatetimeValue());
  const [manualContesto, setManualContesto] = useState("generale");
  const [manualNote, setManualNote] = useState("");
  const [manualHr, setManualHr] = useState("");
  const [savingManual, setSavingManual] = useState(false);

  const loadHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${apiBase}/measures/${fascicoloId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMisurazioni(res.data.misurazioni || []);
    } catch {
      // silent
    }
  }, [apiBase, fascicoloId, token]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadHistory();
      setLoading(false);
    };
    if (token && fascicoloId) init();
  }, [loadHistory, token, fascicoloId]);

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    const value = cfg.parseValue(manualValue);
    if (!cfg.validateValue(value)) {
      toast.error(cfg.valueError);
      return;
    }
    let isoWhen;
    try {
      const d = new Date(manualWhen);
      if (Number.isNaN(d.getTime())) throw new Error("bad");
      isoWhen = d.toISOString();
    } catch {
      toast.error("Data e ora non valide");
      return;
    }
    let battito;
    if (tipo === "ossigenazione") {
      const hrRaw = String(manualHr).trim();
      if (hrRaw !== "") {
        battito = parseInt(hrRaw, 10);
        if (Number.isNaN(battito) || battito < 25 || battito > 220) {
          toast.error("Battito facoltativo: valore tra 25 e 220 bpm");
          return;
        }
      }
    }
    setSavingManual(true);
    try {
      const extra =
        tipo === "glicemia"
          ? { contesto: manualContesto, note: manualNote }
          : { battito };
      const payload = cfg.buildPayload(value, extra, isoWhen);
      const res = await axios.post(`${apiBase}/manual/${fascicoloId}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMisurazioni(res.data.misurazioni || []);
      toast.success("Misurazione registrata");
      setManualValue("");
      setManualNote("");
      setManualHr("");
      setManualWhen(toLocalDatetimeValue());
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        const msg = detail.map((x) => x.msg || x.message || "").filter(Boolean).join(" ");
        toast.error(msg || "Dati non validi");
      } else {
        toast.error(typeof detail === "string" ? detail : "Errore salvataggio");
      }
    } finally {
      setSavingManual(false);
    }
  };

  if (loading) {
    return (
      <Card className="card-base">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className={`w-6 h-6 animate-spin ${cfg.iconClass}`} />
        </CardContent>
      </Card>
    );
  }

  const latest = misurazioni.length > 0 ? misurazioni[0] : null;
  const cls = latest?.classificazione;
  const style = cls ? CLASSIFICATION_STYLES[cls.severity] || CLASSIFICATION_STYLES.normal : null;
  const trend = cfg.getTrend(misurazioni);

  const chartData = [...misurazioni].reverse().map((m) => ({
    data: new Date(m.data_misurazione).toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "2-digit",
    }),
    dataFull: new Date(m.data_misurazione).toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }),
    [cfg.chartKey]: m[cfg.valueKey],
    ...(tipo === "ossigenazione" && m.battito_cardiaco != null
      ? { Battito: m.battito_cardiaco }
      : {}),
  }));

  return (
    <Card className={`card-base ${cfg.borderClass}`}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Icon className={`w-5 h-5 ${cfg.iconClass}`} />
          {cfg.title}
          <Badge variant="outline" className="ml-2 text-xs font-normal">
            Inserimento manuale
          </Badge>
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-6">
        <form
          onSubmit={handleManualSubmit}
          className="p-4 rounded-xl border border-slate-200 bg-white space-y-4"
        >
          <h4 className="font-semibold text-sm text-slate-700 flex items-center gap-2">
            <PenLine className={`w-4 h-4 ${cfg.iconClass}`} />
            Inserimento manuale
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 items-end">
            <div className="space-y-1.5">
              <Label htmlFor={cfg.inputId} className="text-xs text-slate-600">
                {cfg.inputLabel}
              </Label>
              <Input
                id={cfg.inputId}
                type="number"
                inputMode="decimal"
                value={manualValue}
                onChange={(e) => setManualValue(e.target.value)}
                required
                {...cfg.inputProps}
              />
            </div>
            {tipo === "glicemia" && (
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">Contesto</Label>
                <Select value={manualContesto} onValueChange={setManualContesto}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="generale">Generale</SelectItem>
                    <SelectItem value="digiuno">Digiuno</SelectItem>
                    <SelectItem value="post_pasto">Post-pasto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {tipo === "ossigenazione" && (
              <div className="space-y-1.5">
                <Label htmlFor="spo2-hr" className="text-xs text-slate-600">
                  Battito (opz.)
                </Label>
                <Input
                  id="spo2-hr"
                  type="number"
                  inputMode="numeric"
                  min={25}
                  max={220}
                  value={manualHr}
                  onChange={(e) => setManualHr(e.target.value)}
                  placeholder="Es. 72"
                />
              </div>
            )}
            {tipo === "glicemia" && (
              <div className="space-y-1.5 col-span-2">
                <Label htmlFor="glicemia-note" className="text-xs text-slate-600">
                  Note (opz.)
                </Label>
                <Input
                  id="glicemia-note"
                  value={manualNote}
                  onChange={(e) => setManualNote(e.target.value)}
                  placeholder="Es. prima di colazione"
                  maxLength={500}
                />
              </div>
            )}
            <div className="space-y-1.5 col-span-2 sm:col-span-1 lg:col-span-2">
              <Label htmlFor={`${tipo}-when`} className="text-xs text-slate-600">
                Data e ora
              </Label>
              <Input
                id={`${tipo}-when`}
                type="datetime-local"
                value={manualWhen}
                onChange={(e) => setManualWhen(e.target.value)}
                required
              />
            </div>
            <div className="col-span-2 sm:col-span-3 lg:col-span-1 flex sm:justify-end">
              <Button
                type="submit"
                disabled={savingManual}
                variant="secondary"
                className={`w-full sm:w-auto ${cfg.accentBtn}`}
              >
                {savingManual ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Registra misurazione"
                )}
              </Button>
            </div>
          </div>
        </form>

        {misurazioni.length === 0 && (
          <div className="text-center py-6 text-slate-400 border border-dashed border-slate-200 rounded-xl">
            <Activity className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">Nessuna misurazione in archivio</p>
          </div>
        )}

        {latest && (
          <div
            className={`rounded-xl p-5 border ${style?.bg || "bg-slate-50"} ${
              style?.border || "border-slate-200"
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <h4 className={`font-semibold text-sm ${style?.text || "text-slate-600"}`}>
                Ultima misurazione
              </h4>
              <div className="flex items-center gap-2">
                {cls && (
                  <Badge className={`${style?.badge} text-white text-xs`}>{cls.label}</Badge>
                )}
                {trend === "worse" &&
                  (tipo === "ossigenazione" ? (
                    <TrendingDown className="w-4 h-4 text-red-500" />
                  ) : (
                    <TrendingUp className="w-4 h-4 text-red-500" />
                  ))}
                {trend === "better" &&
                  (tipo === "ossigenazione" ? (
                    <TrendingUp className="w-4 h-4 text-green-500" />
                  ) : (
                    <TrendingDown className="w-4 h-4 text-green-500" />
                  ))}
                {trend === "stable" && <Minus className="w-4 h-4 text-slate-400" />}
              </div>
            </div>
            <div className={`${tipo === "ossigenazione" ? "grid grid-cols-2 gap-4" : ""}`}>
              <div className="text-center">
                <p className="text-3xl font-bold text-slate-900">
                  {cfg.renderLatestValue(latest)}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  {cfg.valueLabel} ({cfg.unit})
                </p>
              </div>
              {tipo === "ossigenazione" && latest.battito_cardiaco != null && (
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Heart className="w-5 h-5 text-sky-500" />
                    <p className="text-3xl font-bold text-slate-900">{latest.battito_cardiaco}</p>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Battito (bpm)</p>
                </div>
              )}
            </div>
            <div className="flex items-center gap-1 mt-3 justify-end">
              <Clock className="w-3 h-3 text-slate-400" />
              <span className="text-xs text-slate-400">
                {new Date(latest.data_misurazione).toLocaleString("it-IT")}
              </span>
            </div>
          </div>
        )}

        {chartData.length >= 2 && (
          <div>
            <h4 className="font-semibold text-sm text-slate-600 mb-3">Andamento nel tempo</h4>
            <div className="w-full min-h-[256px]" style={{ height: 256 }}>
              <ResponsiveContainer width="100%" height={256}>
                <LineChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="data" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} domain={["auto", "auto"]} />
                  <Tooltip
                    contentStyle={{
                      borderRadius: "8px",
                      border: "1px solid #e2e8f0",
                      fontSize: "12px",
                    }}
                    labelFormatter={(_, payload) => payload?.[0]?.payload?.dataFull || ""}
                  />
                  <Legend wrapperStyle={{ fontSize: "12px", paddingTop: "8px" }} />
                  <Line
                    type="monotone"
                    dataKey={cfg.chartKey}
                    stroke={cfg.chartColor}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                  {tipo === "ossigenazione" && (
                    <Line
                      type="monotone"
                      dataKey="Battito"
                      stroke="#a855f7"
                      strokeWidth={1.5}
                      strokeDasharray="4 2"
                      dot={{ r: 2 }}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {misurazioni.length > 0 && (
          <div>
            <h4 className="font-semibold text-sm text-slate-600 mb-3">Storico misurazioni</h4>
            <div className="overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b">
                    <th className="text-left px-4 py-2.5 font-medium text-slate-600">Data e Ora</th>
                    <th className="text-center px-4 py-2.5 font-medium text-slate-600">
                      {cfg.valueLabel}
                    </th>
                    {cfg.tableHeaders.map((h) => (
                      <th key={h} className="text-center px-4 py-2.5 font-medium text-slate-600">
                        {h}
                      </th>
                    ))}
                    <th className="text-center px-4 py-2.5 font-medium text-slate-600">Stato</th>
                    <th className="text-center px-4 py-2.5 font-medium text-slate-600">
                      Registrato da
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {misurazioni.map((m, idx) => {
                    const rowCls = m.classificazione;
                    const rowStyle = rowCls
                      ? CLASSIFICATION_STYLES[rowCls.severity]
                      : null;
                    return (
                      <tr
                        key={m.id || idx}
                        className={`border-b last:border-b-0 hover:bg-slate-50 ${
                          idx === 0 ? "bg-slate-50/50" : ""
                        }`}
                      >
                        <td className="px-4 py-2.5 text-slate-700">
                          {new Date(m.data_misurazione).toLocaleString("it-IT", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </td>
                        <td className="text-center px-4 py-2.5 font-semibold">
                          {m[cfg.valueKey] ?? "—"}
                          {tipo === "ossigenazione" ? "%" : ""}
                        </td>
                        {cfg.tableCells(m).map((cell, i) => (
                          <td key={i} className="text-center px-4 py-2.5 text-slate-600">
                            {cell}
                          </td>
                        ))}
                        <td className="text-center px-4 py-2.5">
                          {rowCls ? (
                            <Badge className={`${rowStyle?.badge} text-white text-[10px]`}>
                              {rowCls.label}
                            </Badge>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="text-center px-4 py-2.5 text-slate-500 text-xs">
                          {m.registrato_da || "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MonitoraggioVitaleAdmin(props) {
  const { token } = useAuth();
  return <MonitoraggioVitaleCore {...props} token={token} forOss={false} />;
}

function MonitoraggioVitaleOss(props) {
  const { token } = useOssAuth();
  return <MonitoraggioVitaleCore {...props} token={token} forOss />;
}

export default function MonitoraggioVitale({ tipo, fascicoloId, forOss = false }) {
  if (forOss) {
    return <MonitoraggioVitaleOss tipo={tipo} fascicoloId={fascicoloId} />;
  }
  return <MonitoraggioVitaleAdmin tipo={tipo} fascicoloId={fascicoloId} />;
}
